"use client"

import { observer } from "mobx-react-lite"
import { Line, LineChart } from "recharts"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { type ChartConfig, ChartContainer } from "@/components/ui/chart"
import { getChartColor, getDefaultTheme, useTheme } from "@/hooks/useTheme"
import { cn } from "@/lib/utils"
import type { ThemeConfigType } from "@/stores/DeveloperInsightsStore"

interface QuickInsightCardProps {
  subtitle: string
  date: string
  title: string
  value: string
  secondaryText: string
  chartData: Array<{ value: number }>
  theme?: ThemeConfigType
  className?: string
  onClick?: () => void
  isSelected?: boolean
}

export const QuickInsightCard = observer(
  ({
    subtitle,
    date,
    title,
    value,
    secondaryText,
    chartData,
    theme,
    className,
    onClick,
    isSelected = false,
  }: QuickInsightCardProps) => {
    const activeTheme = theme || getDefaultTheme()
    const themeClasses = useTheme(activeTheme)
    const isDefaultTheme = !theme
    const chartColor = getChartColor(activeTheme, 0)

    const chartConfig = {
      value: {
        label: "Value",
        color: chartColor,
      },
    } satisfies ChartConfig

    return (
      <Card
        className={cn(
          "w-full border-4 transition-[box-shadow,border-color]",
          onClick && "cursor-pointer hover:shadow-md",
          isSelected ? "border-primary shadow-lg" : "border-transparent",
          !isDefaultTheme && themeClasses.card,
          !isDefaultTheme && themeClasses.spacing,
          !isDefaultTheme && themeClasses.borderRadius,
          !isDefaultTheme && themeClasses.colorScheme,
          className,
        )}
        onClick={onClick}
      >
        <CardHeader>
          <div className="flex items-center justify-between">
            <p
              className={cn(
                "text-sm text-muted-foreground",
                !isDefaultTheme && themeClasses.typography,
              )}
            >
              {subtitle}
            </p>
            <time
              className={cn(
                "text-sm text-muted-foreground",
                !isDefaultTheme && themeClasses.typography,
              )}
            >
              {date}
            </time>
          </div>
          <h3 className={cn("text-2xl font-bold mt-2", !isDefaultTheme && themeClasses.typography)}>
            {title}
          </h3>
        </CardHeader>
        <CardContent>
          <div
            className={cn(
              "grid grid-cols-2 gap-6 items-center",
              theme?.spacing === "compact" && "gap-4",
              theme?.spacing === "comfortable" && "gap-8",
            )}
          >
            {/* Left Column - Value */}
            <div className="space-y-1">
              <p className={cn("text-4xl font-bold", !isDefaultTheme && themeClasses.typography)}>
                {value}
              </p>
              <p
                className={cn(
                  "text-sm text-muted-foreground",
                  !isDefaultTheme && themeClasses.typography,
                )}
              >
                {secondaryText}
              </p>
            </div>

            {/* Right Column - Mini Chart */}
            <div className={cn(onClick && "pointer-events-none")}>
              <ChartContainer config={chartConfig} className="h-20 w-full">
                <LineChart data={chartData} accessibilityLayer>
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="var(--color-value)"
                    strokeWidth={2}
                    dot={{
                      r: 2,
                      fill: "var(--color-value)",
                      strokeWidth: 0,
                    }}
                  />
                </LineChart>
              </ChartContainer>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  },
)

QuickInsightCard.displayName = "QuickInsightCard"
